import React, { type JSX,useContext, useId } from 'react';
import { t } from '@lingui/core/macro';

import DatePicker from '@rover/components/src/datetime/DatePicker';
import DayOfWeekPicker from '@rover/components/src/datetime/DayOfWeekPicker';
import GenericCalendar, { CalendarType } from '@rover/components/src/datetime/GenericCalendar';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import { Box, Flex } from '@rover/kibble/core';
import { SPACE_2X, SPACE_6X, SPACE_12X } from '@rover/kibble/tokens/build/rover/es6/DSToken';
import { SearchPageContext } from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchPageContext';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';
import type { Day } from '@rover/types/src/datetime/Day';

type Props = {
  dayOfWeekLabel?: string;
  direction?: 'column' | 'row';
  language: string;
  maxDate?: Date;
  onSelectedDaysChange: (selectedDays: Day[]) => void;
  onStartDateChange: (startDate?: Date | null) => void;
  repeatStartDateLabel?: string;
  selectedDays: Day[];
  serviceTypeSlug?: string | null;
  showLabel: boolean;
  startDate?: Date | null;
  startPlaceholder?: string;
  newCalendarInteraction?: boolean;
  serviceName?: string;
  serviceSubtitle?: string;
  disableValidation?: boolean;
  isMobileBrowser?: boolean;
  onClickFromSearch?: () => void;
  onInputBlur?: (e?: React.FocusEvent<HTMLDivElement, Element>) => void;
  validationError?: string;
  rounded?: boolean;
};

const RepeatServiceScheduler = ({
  dayOfWeekLabel,
  direction = 'column',
  language,
  maxDate,
  onSelectedDaysChange,
  onStartDateChange,
  repeatStartDateLabel,
  selectedDays,
  serviceTypeSlug,
  showLabel,
  startDate,
  startPlaceholder,
  newCalendarInteraction = false,
  serviceName,
  serviceSubtitle,
  disableValidation = false,
  isMobileBrowser = false,
  onClickFromSearch,
  validationError,
  onInputBlur,
  rounded,
}: Props): JSX.Element => {
  const dowPickerId = useId();
  const startDatePickerId = useId();
  const {
    isRollout1SimplifiedMobileOwnerSearchFormExperiment,
    inVariantSimplifiedMobileOwnerSearchFormExperiment,
  } = useContext(SearchPageContext);

  const REPEAT_SERVICE_TYPE_LABELS = (): Record<string, string> => ({
    [SERVICE_TYPE_CHOICES.dropIn]: t`Which days would you like a drop-in?`,
    [SERVICE_TYPE_CHOICES.dayCare]: t`Which days would you like day care?`,
    [SERVICE_TYPE_CHOICES.walking]: t`Which days would you like a walk?`,
    [SERVICE_TYPE_CHOICES.grooming]: t`Which days would you like grooming?`,
  });

  return (
    <Flex
      flexDirection={direction}
      flexGrow="1"
      alignItems={direction === 'column' ? 'flex-end' : 'flex-start'}
    >
      <Flex
        flexDirection="column"
        flexGrow="3"
        pr={direction === 'column' ? '0x' : '3x'}
        width={direction === 'column' ? '100%' : 'auto'}
      >
        <Box mb="3x">
          {serviceTypeSlug && showLabel && (
            <FilterLabel id={dowPickerId}>
              {dayOfWeekLabel || REPEAT_SERVICE_TYPE_LABELS()[serviceTypeSlug]}
            </FilterLabel>
          )}
          <DayOfWeekPicker
            aria-labelledby={dowPickerId}
            language={language}
            selectedDays={selectedDays}
            onChange={onSelectedDaysChange}
            variant={rounded ? 'rounded' : 'default'}
          />
        </Box>
      </Flex>
      <Flex
        flexDirection="column"
        flexGrow="2"
        width={direction === 'column' ? '100%' : 'auto'}
        // @ts-expect-error this usage of pixels is needed to align these two fields
        pb="3px"
      >
        <FilterLabel htmlFor={startDatePickerId}>{repeatStartDateLabel || ''}</FilterLabel>
        {isMobileBrowser ? (
          <GenericCalendar
            calendarType={CalendarType.Single}
            date={startDate}
            id={startDatePickerId}
            language={language}
            maxDate={maxDate}
            onChange={onStartDateChange}
            placeholder={startPlaceholder}
            newCalendarInteraction={newCalendarInteraction}
            serviceName={serviceName}
            serviceSubtitle={serviceSubtitle}
            disableValidation={disableValidation}
            validationType="inline"
            isMobileBrowser={isMobileBrowser}
            {...(isRollout1SimplifiedMobileOwnerSearchFormExperiment &&
              inVariantSimplifiedMobileOwnerSearchFormExperiment && {
                inputStyle: { minHeight: SPACE_12X },
                iconStyle: {
                  height: SPACE_6X,
                  width: SPACE_6X,
                  right: SPACE_2X,
                },
              })}
            onClickFromSearch={onClickFromSearch}
            isDismissible
          />
        ) : (
          <DatePicker
            date={startDate}
            id={startDatePickerId}
            language={language}
            maxDate={maxDate}
            onChange={onStartDateChange}
            placeholder={startPlaceholder}
            newCalendarInteraction={newCalendarInteraction}
            serviceName={serviceName}
            serviceSubtitle={serviceSubtitle}
            disableValidation={disableValidation || false}
            validationType="inline"
            isMobileBrowser={isMobileBrowser}
            {...(isRollout1SimplifiedMobileOwnerSearchFormExperiment &&
              inVariantSimplifiedMobileOwnerSearchFormExperiment && {
                inputStyle: { minHeight: SPACE_12X },
                iconStyle: {
                  height: SPACE_6X,
                  width: SPACE_6X,
                  right: SPACE_2X,
                },
              })}
            onClickFromSearch={onClickFromSearch}
            onInputBlur={onInputBlur}
            validationError={validationError}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default RepeatServiceScheduler;
